import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { getDailyReport } from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';

function todayStr(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Daily Sales Report (feature 15). The selected day's LOCAL boundaries are
 * sent to the server so the report aligns with the tenant's day. Printable via
 * the shared `.receipt-tape` thermal stylesheet.
 */
export function DailyReportPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const [date, setDate] = useState(todayStr());

  const from = new Date(`${date}T00:00:00`);
  const to = new Date(`${date}T23:59:59.999`);

  const query = useQuery({
    queryKey: ['pos-daily-report', branchId, date],
    enabled: !!branchId,
    queryFn: () =>
      getDailyReport({ branchId: branchId!, from: from.toISOString(), to: to.toISOString() }),
  });

  const r = query.data;
  const branchName = branches.find((b) => b.id === branchId)?.name ?? '';

  return (
    <Card>
      <CardHeader className="no-print flex flex-row items-center justify-between">
        <CardTitle>Daily sales report</CardTitle>
        <div className="flex items-end gap-2">
          <BranchPicker branches={branches} branchId={branchId} isScoped={isScoped} onChange={setSelectedBranchId} />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Button size="sm" onClick={() => window.print()} disabled={!r}>
            Print
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !r ? (
          <p className="text-sm text-muted-foreground">No data.</p>
        ) : (
          <div className="receipt-tape font-mono text-sm space-y-2 max-w-md">
            <div className="text-center space-y-0.5 pb-2 border-b border-dashed">
              <div className="font-semibold text-base">Daily Sales Report</div>
              <div className="text-xs">{branchName}</div>
              <div className="text-xs">{date}</div>
            </div>

            <div className="space-y-1">
              <Row label="Orders" value={String(r.ordersCount)} />
              <Row label="Gross sales" value={`₱${Number(r.grossSales).toFixed(2)}`} bold />
              <Row label="Total PV" value={Number(r.pvTotal).toFixed(2)} />
              <Row label="Refunds" value={`${r.refundsCount} · ₱${Number(r.refundsTotal).toFixed(2)}`} />
            </div>

            <div className="border-t border-dashed pt-2 space-y-1">
              <div className="text-xs font-semibold">PAYMENTS</div>
              {r.paymentBreakdown.length ? (
                r.paymentBreakdown.map((p) => (
                  <Row
                    key={p.method}
                    label={`${p.method} (${p.count})`}
                    value={`₱${Number(p.total).toFixed(2)}`}
                  />
                ))
              ) : (
                <div className="text-xs text-muted-foreground">No payments.</div>
              )}
            </div>

            <div className="border-t border-dashed pt-2 space-y-1">
              <div className="text-xs font-semibold">TOP ITEMS</div>
              {r.topItems.length ? (
                r.topItems.map((it) => (
                  <Row
                    key={`${it.productId}:${it.variantName ?? ''}`}
                    label={`${it.variantName ? `${it.name} — ${it.variantName}` : it.name} ×${it.qty}`}
                    value={`₱${Number(it.total).toFixed(2)}`}
                  />
                ))
              ) : (
                <div className="text-xs text-muted-foreground">No items sold.</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? 'font-semibold' : ''}`}>
      <span className="capitalize">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
